import DeckPlayerHost from '../components/DeckPlayerHost.vue'
import { mountDeckPlayerWidget } from './mountDeckPlayerWidget'
import type { MountDeckPlayerWidgetHandle } from './mountDeckPlayerWidget'

import type { MountTarget, MountDeckPlayerOptions, MountDeckPlayerHandle } from '../../../../../shared/player-contracts/src'
export type { MountDeckPlayerOptions, MountDeckPlayerHandle } from '../../../../../shared/player-contracts/src'

const resolveTarget = (target: MountTarget): Element | null => {
  if (typeof target === 'string') {
    return document.querySelector(target)
  }
  return target
}

const normalizeModel = (value: unknown) => {
  const str = typeof value === 'string' ? value : ''
  if (str === 'checkbox') return 'checkbox'
  if (str === 'anki') return 'anki'
  if (str === 'flowchart') return 'flowchart'
  return 'flowchart'
}

export async function mountDeckPlayer(
  target: MountTarget,
  options: MountDeckPlayerOptions,
): Promise<MountDeckPlayerHandle | undefined> {
  const el = resolveTarget(target)
  if (!el) {
    console.warn('PerlingoFlashcards.mountDeckPlayer: target not found', target)
    return
  }

  const deckId = String(options.deckId ?? '').trim()
  if (!deckId) {
    console.warn('PerlingoFlashcards.mountDeckPlayer: missing deckId', options.deckId)
    return
  }

  // Unmount prior instance on the same element.
  const previousUnmount = (el as unknown as { __pfcUnmount?: (() => void) | undefined })
    .__pfcUnmount
  if (previousUnmount) {
    try {
      previousUnmount()
    } catch {
      // ignore
    }
  }

  const model = normalizeModel(options.model)

  let widgetHandle: MountDeckPlayerWidgetHandle
  try {
    widgetHandle = await mountDeckPlayerWidget(el, DeckPlayerHost, {
      displayName: 'deckplayer-shortcode',
      allowRemount: true,
      props: {
        deckId,
        model,
        passthroughProps: options.props ?? {},
        restRoot: options.restRoot,
        nonce: options.nonce,
        onPhaseChange: options.onPhaseChange,
        onComplete: options.onLevelComplete ?? options.onComplete,
        onLoad: options.onLoad,
        onShowMedia: options.onShowMedia,
        mediaMaterials: options.mediaMaterials,
        flowContext: options.flowContext,
        onMediaPause: options.onMediaPause,
        onAwaitingStart: options.onAwaitingStart,
        suppressCompletionUi: options.suppressCompletionUi,
        overrideFlowGraph: options.flowGraphOverride as
          | import('../flowchart/flowGraph').FlowGraphV1
          | null
          | undefined,
      },
    })
  } catch (e) {
    console.warn('PerlingoFlashcards.mountDeckPlayer: failed to mount', e)
    return
  }

  const handle: MountDeckPlayerHandle = {
    unmount: () => widgetHandle.unmount(),

    updateScale(scale: number) {
      widgetHandle.updateProps({
        passthroughProps: { ...(options.props ?? {}), scale },
      })
    },

    getRenderedSize() {
      const cardEl = el.querySelector('.card-scale-outer')
      if (cardEl) {
        const rect = cardEl.getBoundingClientRect()
        if (!rect.width) return null
        const scale = parseFloat(cardEl.getAttribute('data-pfc-scale') ?? '') || 0
        return { width: rect.width, height: rect.height, scale }
      }
      const preStartEl = el.querySelector('.pre-start[data-pfc-scale]')
      if (preStartEl) {
        const rect = preStartEl.getBoundingClientRect()
        if (!rect.width) return null
        const scale = parseFloat(preStartEl.getAttribute('data-pfc-scale') ?? '') || 0
        return { width: rect.width, height: rect.height, scale }
      }
      return null
    },
  }

  ;(el as unknown as { __pfcUnmount?: (() => void) | undefined }).__pfcUnmount = () =>
    handle.unmount()

  return handle
}
